import { useState, useEffect } from 'react';
import axios from 'axios';
import type { SteamUser, UserGameStats } from '../types';

export function useSteamUser(username: string) {
  const [user, setUser] = useState<SteamUser | null>(null);
  const [gameStats, setGameStats] = useState<UserGameStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;
    const controller = new AbortController();

    const fetchUserData = async () => {
      try {
        const response = await axios.get(`/.netlify/functions/getSteamUser`, {
          params: { username },
          signal: controller.signal
        });

        if (!isMounted) return;

        if (response.data.error) {
          setError(response.data.error);
          return;
        }

        setUser(response.data.user);
        setGameStats(response.data.gameStats);
        setError(null);
      } catch (err) {
        if (!isMounted) return;
        console.error('Error fetching Steam user:', err);
        setError('Failed to load user data');
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    if (username) {
      setIsLoading(true);
      fetchUserData();
    }

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [username]);

  return { user, gameStats, isLoading, error };
}